import React, { useMemo, useRef, useState } from 'react';
import { XMarkIcon, CameraIcon, XCircleIcon } from '@heroicons/react/24/outline';
import {
  useListPickupContactsQuery,
  useSessionCheckOutMutation,
  useSessionMultiCheckOutMutation,
  useGetUploadUrlMutation,
  useSessionVoidCheckInMutation,
} from '../../store/api/localApi';
import { useToast } from '../Toast';
import type { PickupContactShape, AttendanceEventShape } from '../../types/attendance-v2';

interface PickupModalProps {
  sessionId: string;
  primaryStudentId: string;
  primaryStudentName: string;
  otherCheckedInStudentIds: string[];
  siblingContactsByStudentId: Record<string, PickupContactShape[]>;
  onClose: () => void;
  onCheckedOut?: (events: AttendanceEventShape[]) => void;
}

/**
 * Four-step modal:
 *   1. Pick a contact (or "Other").
 *   2. Sibling step — if the chosen contact also covers other checked-in kids.
 *   3. "Other" step — name + verification note for an unlisted person.
 *   4. [NEW] Confirm + optional photo — shown after picking a contact (no
 *      siblings) or after the sibling selection. Staff can snap a photo
 *      before hitting Release. Skipping photo is fine.
 */
const PickupModal: React.FC<PickupModalProps> = ({
  sessionId,
  primaryStudentId,
  primaryStudentName,
  otherCheckedInStudentIds,
  siblingContactsByStudentId,
  onClose,
  onCheckedOut,
}) => {
  const { showError, showSuccess } = useToast();
  const [singleCheckOut, { isLoading: singleLoading }] = useSessionCheckOutMutation();
  const [multiCheckOut, { isLoading: multiLoading }] = useSessionMultiCheckOutMutation();
  const [voidCheckIn, { isLoading: voidingLoading }] = useSessionVoidCheckInMutation();
  const [getUploadUrl] = useGetUploadUrlMutation();
  const loading = singleLoading || multiLoading || voidingLoading;

  const { data: contactsResponse } = useListPickupContactsQuery(primaryStudentId);
  const contacts = contactsResponse?.contacts ?? [];

  const [mode, setMode] = useState<'release' | 'mark-absent'>('release');

  // Payload-only shapes: they are INTERSECTED with a `{ kind: ... }` member of
  // `Step` below, so they must not carry a `kind` of their own. Declaring one
  // here (e.g. `kind: 'contact'`) intersects two disjoint string literals and
  // silently collapses those Step variants to `never`, which is what made every
  // `step.contact` access a type error.
  type ModeContact = { contact: PickupContactShape };
  type ModeOther = { name: string; verificationNote: string };
  type Step =
    | { kind: 'pick' }
    | ({ kind: 'sibling' } & ModeContact)
    | ({ kind: 'other-form' } & Partial<ModeOther>)
    | ({ kind: 'confirm-single' } & ModeContact)
    | ({ kind: 'confirm-multi'; studentIds: string[] } & ModeContact)
    | { kind: 'mark-absent-confirm' };

  const [step, setStep] = useState<Step>({ kind: 'pick' });
  const [selectedSiblingIds, setSelectedSiblingIds] = useState<Set<string>>(
    new Set([primaryStudentId])
  );

  // Photo state — shared across confirm steps.
  const [photoS3Key, setPhotoS3Key] = useState<string | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearPhoto = () => {
    setPhotoS3Key(null);
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePhotoSelect = async (file: File) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const preview = URL.createObjectURL(file);
      setPhotoPreviewUrl(preview);
      const { uploadUrl, s3Key } = await getUploadUrl({
        purpose: 'PICKUP_PHOTO',
        contextId: sessionId,
        mimeType: file.type,
        sizeBytes: file.size,
      }).unwrap();
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      setPhotoS3Key(s3Key);
    } catch {
      showError('Could not upload photo — you can still release without one.');
      clearPhoto();
    } finally {
      setPhotoUploading(false);
    }
  };

  // Sibling detection.
  //
  // Only a registered guardian is a provable shared identity across siblings
  // (guardian id === parent userId). Extra contacts are per-child UUIDs with no
  // shared identity — never batch them (a name twin could be a half-sibling's
  // other household). Drop the old name+relationshipType fallback entirely.
  const siblingsAuthorisedFor = useMemo(() => {
    if (step.kind !== 'sibling') return [];
    const matchesSameGuardian = (c: PickupContactShape) =>
      step.contact.isGuardian && c.isGuardian && c.id === step.contact.id;
    return otherCheckedInStudentIds.filter((sid) =>
      (siblingContactsByStudentId[sid] ?? []).some(matchesSameGuardian)
    );
  }, [step, otherCheckedInStudentIds, siblingContactsByStudentId]);

  const pickContact = (contact: PickupContactShape) => {
    clearPhoto();
    const matchesSameGuardian = (c: PickupContactShape) =>
      contact.isGuardian && c.isGuardian && c.id === contact.id;
    const hasSiblings = otherCheckedInStudentIds.some((sid) =>
      (siblingContactsByStudentId[sid] ?? []).some(matchesSameGuardian)
    );
    if (hasSiblings) {
      setStep({ kind: 'sibling', contact });
      setSelectedSiblingIds(new Set([primaryStudentId]));
    } else {
      setStep({ kind: 'confirm-single', contact });
    }
  };

  const proceedToConfirmMulti = (contact: PickupContactShape) => {
    clearPhoto();
    setStep({ kind: 'confirm-multi', contact, studentIds: Array.from(selectedSiblingIds) });
  };

  const releaseSingle = async (contact: PickupContactShape) => {
    try {
      const result = await singleCheckOut({
        sessionId,
        studentId: primaryStudentId,
        releasedTo: { contactId: contact.id },
        pickupPhotoS3Key: photoS3Key ?? undefined,
      }).unwrap();
      showSuccess(`${primaryStudentName} released to ${contact.name}.`);
      onCheckedOut?.([result.event]);
      onClose();
    } catch (err: any) {
      showError(err?.data?.message || err?.data?.error || 'Could not record pickup.');
    }
  };

  const releaseMany = async (contact: PickupContactShape, studentIds: string[]) => {
    if (studentIds.length === 0) return;
    if (studentIds.length === 1) {
      await releaseSingle(contact);
      return;
    }
    try {
      const result = await multiCheckOut({
        sessionId,
        studentIds,
        releasedTo: { contactId: contact.id },
        pickupPhotoS3Key: photoS3Key ?? undefined,
      }).unwrap();
      showSuccess(`${studentIds.length} kids released to ${contact.name}.`);
      onCheckedOut?.(result.events);
      onClose();
    } catch (err: any) {
      showError(err?.data?.message || err?.data?.error || 'Could not record pickup.');
    }
  };

  const releaseOther = async (name: string, verificationNote: string) => {
    if (!name.trim() || !verificationNote.trim()) {
      showError('Name and verification note are both required for an "Other" release.');
      return;
    }
    try {
      const result = await singleCheckOut({
        sessionId,
        studentId: primaryStudentId,
        releasedTo: { name: name.trim(), verificationNote: verificationNote.trim() },
        pickupPhotoS3Key: photoS3Key ?? undefined,
      }).unwrap();
      showSuccess(`${primaryStudentName} released to ${name.trim()} — flagged for follow-up.`);
      onCheckedOut?.([result.event]);
      onClose();
    } catch (err: any) {
      showError(err?.data?.message || err?.data?.error || 'Could not record pickup.');
    }
  };

  // ===== render =====
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="pickup-modal"
    >
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <div
            className="flex items-center gap-2 rounded-lg bg-slate-100 p-1 mb-4"
            data-testid="pickup-mode-toggle"
          >
            <button
              className={`flex-1 rounded-md py-1.5 text-sm font-semibold transition-colors ${
                mode === 'release' ? 'bg-white shadow text-slate-900' : 'text-slate-500'
              }`}
              onClick={() => {
                setMode('release');
                setStep({ kind: 'pick' });
              }}
              data-testid="pickup-mode-release"
            >
              Release {primaryStudentName}
            </button>
            <button
              className={`flex-1 rounded-md py-1.5 text-sm font-semibold transition-colors ${
                mode === 'mark-absent' ? 'bg-white shadow text-slate-900' : 'text-slate-500'
              }`}
              onClick={() => {
                setMode('mark-absent');
                setStep({ kind: 'mark-absent-confirm' });
              }}
              data-testid="pickup-mode-mark-absent"
            >
              Mark absent
            </button>
          </div>

          {mode === 'release' && step.kind === 'pick' && (
            <PickStep
              primaryStudentName={primaryStudentName}
              contacts={contacts}
              onPickContact={pickContact}
              onPickOther={() => {
                clearPhoto();
                setStep({ kind: 'other-form' });
              }}
              onClose={onClose}
            />
          )}
          {mode === 'release' && step.kind === 'sibling' && (
            <SiblingStep
              contact={step.contact}
              primaryStudentId={primaryStudentId}
              primaryStudentName={primaryStudentName}
              siblingIds={siblingsAuthorisedFor}
              siblingContactsByStudentId={siblingContactsByStudentId}
              selected={selectedSiblingIds}
              onToggle={(sid) =>
                setSelectedSiblingIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(sid)) next.delete(sid);
                  else next.add(sid);
                  return next;
                })
              }
              onBack={() => setStep({ kind: 'pick' })}
              onProceed={() => proceedToConfirmMulti(step.contact)}
            />
          )}
          {mode === 'release' && step.kind === 'confirm-single' && (
            <ConfirmStep
              studentLabel={primaryStudentName}
              contact={step.contact}
              photoPreviewUrl={photoPreviewUrl}
              photoUploading={photoUploading}
              photoS3Key={photoS3Key}
              fileInputRef={fileInputRef}
              onPhotoSelect={handlePhotoSelect}
              onClearPhoto={clearPhoto}
              onBack={() => setStep({ kind: 'pick' })}
              onRelease={() => releaseSingle(step.contact)}
              loading={loading}
            />
          )}
          {mode === 'release' && step.kind === 'confirm-multi' && (
            <ConfirmStep
              studentLabel={`${step.studentIds.length} kids`}
              contact={step.contact}
              photoPreviewUrl={photoPreviewUrl}
              photoUploading={photoUploading}
              photoS3Key={photoS3Key}
              fileInputRef={fileInputRef}
              onPhotoSelect={handlePhotoSelect}
              onClearPhoto={clearPhoto}
              onBack={() => setStep({ kind: 'sibling', contact: step.contact })}
              onRelease={() => releaseMany(step.contact, step.studentIds)}
              loading={loading}
            />
          )}
          {mode === 'release' && step.kind === 'other-form' && (
            <OtherStep
              primaryStudentName={primaryStudentName}
              photoPreviewUrl={photoPreviewUrl}
              photoUploading={photoUploading}
              photoS3Key={photoS3Key}
              fileInputRef={fileInputRef}
              onPhotoSelect={handlePhotoSelect}
              onClearPhoto={clearPhoto}
              onBack={() => setStep({ kind: 'pick' })}
              onRelease={releaseOther}
              loading={loading}
            />
          )}
          {mode === 'mark-absent' && step.kind === 'mark-absent-confirm' && (
            <MarkAbsentConfirmStep
              studentName={primaryStudentName}
              onConfirm={async (reason) => {
                try {
                  await voidCheckIn({ sessionId, studentId: primaryStudentId, reason }).unwrap();
                  showSuccess(`${primaryStudentName} marked absent.`);
                  onClose();
                } catch (err: any) {
                  showError(err?.data?.message || err?.data?.error || 'Could not mark absent.');
                }
              }}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------

const PickStep: React.FC<{
  primaryStudentName: string;
  contacts: PickupContactShape[];
  onPickContact: (c: PickupContactShape) => void;
  onPickOther: () => void;
  onClose: () => void;
}> = ({ primaryStudentName, contacts, onPickContact, onPickOther, onClose }) => (
  <>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-base font-semibold text-slate-900">Release {primaryStudentName}</h2>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="text-slate-400 hover:text-slate-600"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
    <p className="text-sm text-slate-600 mb-3">Tap the person collecting.</p>
    <ul className="space-y-2 max-h-80 overflow-y-auto">
      {contacts.map((contact) => (
        <li key={contact.id}>
          <button
            type="button"
            data-testid={`pickup-pick-${contact.id}`}
            onClick={() => onPickContact(contact)}
            className="w-full p-3 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 text-left flex items-center gap-3 transition"
          >
            <Avatar name={contact.name} tone={contact.isGuardian ? 'emerald' : 'amber'} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{contact.name}</p>
              <p className="text-xs text-slate-500 truncate">{contact.relationship}</p>
            </div>
          </button>
        </li>
      ))}
      <li>
        <button
          type="button"
          data-testid="pickup-pick-other"
          onClick={onPickOther}
          className="w-full p-3 rounded-lg border border-dashed border-slate-300 hover:bg-slate-50 text-left flex items-center gap-3"
        >
          <div className="h-9 w-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-sm flex-shrink-0">
            ?
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700">Other / not on this list</p>
            <p className="text-xs text-slate-500">Will need a name and verification</p>
          </div>
        </button>
      </li>
    </ul>
  </>
);

const SiblingStep: React.FC<{
  contact: PickupContactShape;
  primaryStudentId: string;
  primaryStudentName: string;
  siblingIds: string[];
  siblingContactsByStudentId: Record<string, PickupContactShape[]>;
  selected: Set<string>;
  onToggle: (sid: string) => void;
  onBack: () => void;
  onProceed: () => void;
}> = ({
  contact,
  primaryStudentId,
  primaryStudentName,
  siblingIds,
  siblingContactsByStudentId,
  selected,
  onToggle,
  onBack,
  onProceed,
}) => {
  const nameForStudent = (sid: string): string => {
    const list = siblingContactsByStudentId[sid] ?? [];
    const guardian = list.find((c) => c.isGuardian);
    return guardian ? `Sibling of ${guardian.name.split(' ')[0]}` : 'Sibling';
  };
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-900">Anyone else with {contact.name}?</h2>
        <button type="button" onClick={onBack} className="text-xs text-slate-500 hover:underline">
          ← Back
        </button>
      </div>
      <p className="text-sm text-slate-600 mb-3">
        {contact.name} is also authorised for these kids who are checked in now.
      </p>
      <ul className="space-y-2">
        <li>
          <label className="w-full p-3 rounded-lg border-2 border-emerald-300 bg-emerald-50 flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked disabled className="w-5 h-5 rounded text-emerald-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900">{primaryStudentName}</p>
              <p className="text-xs text-slate-500">The kid you tapped</p>
            </div>
          </label>
        </li>
        {siblingIds.map((sid) => (
          <li key={sid}>
            <label
              className={`w-full p-3 rounded-lg border-2 flex items-center gap-3 cursor-pointer transition ${
                selected.has(sid)
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-slate-200 hover:border-emerald-300'
              }`}
              data-testid={`pickup-sibling-${sid}`}
            >
              <input
                type="checkbox"
                checked={selected.has(sid)}
                onChange={() => onToggle(sid)}
                className="w-5 h-5 rounded text-emerald-600"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">{nameForStudent(sid)}</p>
              </div>
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={onProceed}
          data-testid="pickup-sibling-next"
          className="px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
        >
          Next →
        </button>
      </div>
    </>
  );
};

const ConfirmStep: React.FC<{
  studentLabel: string;
  contact: PickupContactShape;
  photoPreviewUrl: string | null;
  photoUploading: boolean;
  photoS3Key: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onPhotoSelect: (file: File) => void;
  onClearPhoto: () => void;
  onBack: () => void;
  onRelease: () => void;
  loading: boolean;
}> = ({
  studentLabel,
  contact,
  photoPreviewUrl,
  photoUploading,
  photoS3Key,
  fileInputRef,
  onPhotoSelect,
  onClearPhoto,
  onBack,
  onRelease,
  loading,
}) => (
  <>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-base font-semibold text-slate-900">
        Release {studentLabel} to {contact.name}
      </h2>
      <button type="button" onClick={onBack} className="text-xs text-slate-500 hover:underline">
        ← Back
      </button>
    </div>

    <PhotoCapture
      previewUrl={photoPreviewUrl}
      uploading={photoUploading}
      s3Key={photoS3Key}
      fileInputRef={fileInputRef}
      onSelect={onPhotoSelect}
      onClear={onClearPhoto}
    />

    <div className="mt-5 flex items-center gap-2 justify-end">
      <button
        type="button"
        onClick={onRelease}
        disabled={loading || photoUploading}
        data-testid="pickup-confirm-release"
        className="px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
      >
        {loading ? 'Releasing…' : `Release to ${contact.name.split(' ')[0]}`}
      </button>
    </div>
  </>
);

const OtherStep: React.FC<{
  primaryStudentName: string;
  photoPreviewUrl: string | null;
  photoUploading: boolean;
  photoS3Key: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onPhotoSelect: (file: File) => void;
  onClearPhoto: () => void;
  onBack: () => void;
  onRelease: (name: string, verificationNote: string) => void;
  loading: boolean;
}> = ({
  primaryStudentName,
  photoPreviewUrl,
  photoUploading,
  photoS3Key,
  fileInputRef,
  onPhotoSelect,
  onClearPhoto,
  onBack,
  onRelease,
  loading,
}) => {
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold text-slate-900">Other pickup</h2>
        <button type="button" onClick={onBack} className="text-xs text-slate-500 hover:underline">
          ← Back
        </button>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900">
        <strong>This will be flagged.</strong> The parent will see a confirmation prompt; admin sees
        this in the audit log.
      </div>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Their name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sarah Walsh"
            data-testid="pickup-other-name"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            How are they verified?
          </label>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Parent rang to authorise. Showed ID."
            data-testid="pickup-other-note"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        <PhotoCapture
          previewUrl={photoPreviewUrl}
          uploading={photoUploading}
          s3Key={photoS3Key}
          fileInputRef={fileInputRef}
          onSelect={onPhotoSelect}
          onClear={onClearPhoto}
        />
      </div>
      <div className="mt-5 flex items-center gap-2 justify-end">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-slate-500 hover:underline px-3 py-1.5"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onRelease(name, note)}
          disabled={loading || photoUploading || !name.trim() || !note.trim()}
          data-testid="pickup-other-release"
          className="px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
        >
          {loading ? 'Releasing…' : `Release ${primaryStudentName.split(' ')[0]} & flag`}
        </button>
      </div>
    </>
  );
};

const MarkAbsentConfirmStep: React.FC<{
  studentName: string;
  onConfirm: (reason: string) => void;
  loading: boolean;
}> = ({ studentName, onConfirm, loading }) => {
  const [reason, setReason] = useState('Accidental check-in');
  return (
    <div className="space-y-4" data-testid="pickup-mark-absent-confirm">
      <p className="text-xs text-slate-500">
        {studentName}'s check-in will be removed from the attendance log.
      </p>
      <div>
        <label className="text-xs text-slate-600">Reason</label>
        <input
          className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="pickup-mark-absent-reason"
        />
      </div>
      <button
        onClick={() => onConfirm(reason)}
        disabled={loading || !reason.trim()}
        className="w-full rounded bg-red-600 py-2 text-sm font-semibold text-white disabled:opacity-50"
        data-testid="pickup-mark-absent-yes"
      >
        {loading ? 'Removing…' : 'Confirm mark absent'}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared photo capture widget
// ---------------------------------------------------------------------------

const PhotoCapture: React.FC<{
  previewUrl: string | null;
  uploading: boolean;
  s3Key: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onSelect: (file: File) => void;
  onClear: () => void;
}> = ({ previewUrl, uploading, s3Key, fileInputRef, onSelect, onClear }) => (
  <div className="mt-3">
    <input
      ref={fileInputRef}
      type="file"
      accept="image/jpeg,image/png,image/heic"
      capture="environment"
      className="hidden"
      data-testid="pickup-event-photo-input"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) onSelect(file);
      }}
    />
    {previewUrl ? (
      <div className="flex items-center gap-3">
        <img
          src={previewUrl}
          alt="Pickup photo preview"
          data-testid="pickup-event-photo-preview"
          className="h-16 w-16 rounded-lg object-cover border border-slate-200"
        />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-600 truncate">
            {uploading ? 'Uploading…' : s3Key ? 'Photo ready' : 'Upload failed'}
          </p>
          {!uploading && (
            <button
              type="button"
              onClick={onClear}
              data-testid="pickup-event-photo-remove"
              className="mt-1 text-xs text-rose-500 hover:text-rose-700 inline-flex items-center gap-1"
            >
              <XCircleIcon className="h-3.5 w-3.5" />
              Remove
            </button>
          )}
        </div>
      </div>
    ) : (
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        data-testid="pickup-event-photo-btn"
        className="w-full py-2 px-3 rounded-lg border border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50 text-sm text-slate-500 hover:text-emerald-700 flex items-center justify-center gap-2 transition"
      >
        <CameraIcon className="h-4 w-4" />
        Add pickup photo <span className="text-slate-400">(optional)</span>
      </button>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

const Avatar: React.FC<{ name: string; tone: 'emerald' | 'amber' }> = ({ name, tone }) => {
  const initials =
    name
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  const cls =
    tone === 'emerald' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
  return (
    <div
      aria-hidden="true"
      className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${cls}`}
    >
      {initials}
    </div>
  );
};

export default PickupModal;
